<template>
  <b-card
    no-body
    class="border"
  >
    <b-card-header class="p-1">
      <b-card-title class="font-medium-2">
        <feather-icon
          icon="SettingsIcon"
          size="18"
        />
        <span class="align-middle ml-50">{{ countryCode.toUpperCase() }} user settings</span>
      </b-card-title>
    </b-card-header>
    <validation-observer ref="customsValidation">

      <b-card-body v-if="countryCode === 'ee'">
        <b-row>
          <b-col cols="6">
            <b-form-group
              label="Agent number"
              label-for="agent-number"
            >
              <validation-provider
                #default="{ errors }"
                name="Agent number"
              >
                <b-form-input
                  id="agent-number"
                  v-model="formData.ee.agentNumber"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
      <b-card-body v-if="countryCode === 'lv'">
        <b-card-body>
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="EDS Username"
                label-for="eds-username"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Eds Username"
                  :rules="formData.lv.edsPassword ? 'required' : ''"
                >
                  <b-form-input
                    id="eds-username"
                    v-model="formData.lv.edsUser"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="EDS Password"
                label-for="eds-password"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Eds Password"
                  :rules="formData.lv.edsUser ? 'required' : ''"
                >
                  <b-form-input
                    id="eds-password"
                    v-model="formData.lv.edsPassword"
                    :type="'password'"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card-body>
      <b-card-body v-if="countryCode === 'lt'">
        <div>
          <p>
            👉 Instructions on how to create and extract the certificate and private key can be found in
            <a
              href="https://bap.lrmuitine.lt/bap-app/"
              target="_blank"
            >BAP (Lithuanian Customs Portal)</a>.
          </p>
          <p>
            You may upload one of the following:
          </p>
          <ul>
            <li>A certificate with the exported private key in a <b>single file (.pfx format)</b>.</li>
            <li>A certificate and its private key as <b>two separate files</b> (select or drag and drop them together).
            </li>
          </ul>
          <p>If any of the files is encrypted, please provide the password.</p>

          <h6>Important:</h6>
          <div>
            The private individual to whom the certificate is issued must have the following permissions granted by the
            company in <b>BAP:</b>
          </div>
          <ul>
            <li><b>Business Profile:</b> "iMDAS_LVC"</li>
            <li><b>Rights Granted:</b> "Data exchange of electronic low-value consignment declarations with Lithuanian
              Customs
              and monitoring of their customs clearance"
            </li>
          </ul>
          <div v-if="hasSoonExpiringCertificate && hasValidCertificate">
            <b-alert
              show
              variant="warning"
              class="p-1"
            >
              Certificate will expire soon {{ formData.lt.certValidTo }} utc
            </b-alert>
          </div>
          <div v-else-if="hasValidCertificate">
            <b-alert
              show
              variant="success"
              class="p-1"
            >
              Certificate is valid until {{ formData.lt.certValidTo }} utc
            </b-alert>
          </div>
          <div v-else>
            <b-alert
              show
              variant="danger"
              class="p-1"
            >
              No valid certificate.
              <span v-if="formData.lt.certValidTo">Certificate expired on {{ formData.lt.certValidTo }} utc</span>
            </b-alert>
          </div>
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Certificate and Private key Files"
                label-for="certFiles"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Certificate Files"
                  rules=""
                >
                  <b-form-file
                    id="certFiles"
                    v-model="formData.lt.certFiles"
                    accept=".pem,.crt,.pfx,.txt,.cer"
                    placeholder="Upload .pfx .pem .crt .cer .txt files"
                    drop-placeholder="Drop files here..."
                    multiple
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <div>
                    <small
                      v-if="formData.lt.certUpdatedAt"
                      class="text-muted"
                    >Last updated time {{ formData.lt.certUpdatedAt }} utc</small>
                  </div>
                </validation-provider>

              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Password"
                label-for="password"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  :rules="''"
                >
                  <b-form-input
                    id="password"
                    v-model="formData.lt.password"
                    v-b-tooltip.hover.top="'Passphrase to unlock .pfx and encrypted private key files'"
                    :state="errors.length > 0 ? false : null"
                    type="password"
                    placeholder="Enter password here"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </validation-observer>
  </b-card>
</template>

<script>
import {
  BAlert,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BCol,
  BFormFile,
  BFormGroup,
  BFormInput,
  BRow,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import handleError from '@/views/components/errorHandler'
import { EventBus } from '@core/utils/utils'
import moment from 'moment'

localize({
  en: {
    fields: {
      'Eds Username': {
        required: 'EDS Username is required if EDS Password is provided!',
      },
      'Eds Password': {
        required: 'EDS Password is required if EDS Username is provided!',
      },
    },
  },
})

export default {
  components: {
    BAlert,
    BFormFile,
    BCard,
    BCardBody,
    BCardTitle,
    BCardHeader,
    BCol,
    BRow,
    BFormGroup,
    BFormInput,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    countryCode: {
      type: String,
      required: true,
    },
    userId: {
      type: Number,
      required: true,
    },
    companyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      formData: {
        ee: {
          agentNumber: '',
        },
        lv: {
          edsUser: '',
          edsPassword: '',
        },
        lt: {
          certFiles: [],
          password: '',
          certValidTo: '',
          certId: '',
          certUpdatedAt: '',
        },
      },
    }
  },
  computed: {
    hasValidCertificate() {
      return moment(this.formData.lt.certValidTo) > moment()
    },
    hasSoonExpiringCertificate() {
      const daysUntilExpiry = moment(this.formData.lt.certValidTo).diff(moment(), 'days')
      return daysUntilExpiry < 30
    },
  },
  created() {
    EventBus.$on('saveCustomsSettings', this.saveChanges)
    this.fetchUserCustomsData()
  },
  beforeDestroy() {
    EventBus.$off('saveCustomsSettings', this.saveChanges)
  },
  methods: {
    saveChanges() {
      this.$refs.customsValidation.validate().then(success => {
        if (!success) {
          return
        }

        let formData = this.formData[this.countryCode]
        let config = {}

        if (this.countryCode === 'lt') {
          if (this.formData.lt.certFiles.length === 0) {
            return
          }
          formData = new FormData()
          this.formData.lt.certFiles.forEach((file, i) => {
            formData.append(`files[${i}]`, file)
          })
          formData.append('password', this.formData.lt.password)
          config = {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        }

        this.$http.post(`/v1/user/${this.userId}/customs/${this.countryCode}`, formData, config)
          .then(response => {
            if (this.countryCode === 'lt') {
              this.fetchUserCustomsData()
            }

            this.$toast({
              component: ToastificationContent,
              props: {
                title: response.data.message,
                icon: 'EditIcon',
                variant: 'success',
              },
            })
          })
          .catch(error => {
            handleError(error, this.$toast)
          })
      })
    },
    fetchUserCustomsData() {
      this.$http.get(`/v1/user/${this.userId}/customs/${this.countryCode}`)
        .then(response => {
          switch (this.countryCode) {
            case 'ee':
              this.$data.formData.ee = response.data
              break
            case 'lv':
              this.$data.formData.lv = response.data
              break
            case 'lt':
              if (response.data.certificate) {
                this.$data.formData.lt.certValidTo = response.data.certificate.validTo
                this.$data.formData.lt.certUpdatedAt = response.data.certificate.updatedAt
                this.$data.formData.lt.certId = response.data.certificate.id
              }
              break
            default:
              break
          }
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
  },
}
</script>
